import { useEffect, useRef, useState } from 'react';

import {
  type ElementoSondavel,
  SONDA_TENTATIVAS,
  sondarSegundoDecoder,
} from './decoderProbe';

/** `readyState` em que o vídeo ativo já decodificou um quadro — ou seja, já tem um decoder. */
const QUADRO_DECODIFICADO = 2;

/** Quanto esperamos o vídeo ativo começar antes de desistir de sondar nesta mídia. */
const ESPERA_PELO_ATIVO_MS = 500;
const TENTATIVAS_DE_ESPERA = 24;

export type VereditoDaSonda = 'pendente' | 'ligado' | 'desligado';

/**
 * Quantos decoders de vídeo o motor está usando AGORA — 2 com o A/B ligado, 1 sem ele.
 * `null` onde o A/B não se aplica (Tizen/webOS sob AVPlay, velocidade ≠ 1): lá o vídeo não
 * passa pelo `<video>` do DOM, então responder um número seria inventar.
 *
 * <p>Módulo, e não estado do React, porque quem lê é o snapshot de saúde do `SyncService` —
 * um serviço, fora da árvore. Mesmo padrão de `lerSseZumbis()`/`hostAarVersion()`.</p>
 */
let decodersDeVideoEmUso: 1 | 2 | null = null;

/** Lido pelo snapshot de saúde do sync. */
export function lerDecodersDeVideoEmUso(): 1 | 2 | null {
  return decodersDeVideoEmUso;
}

/** Só para teste: devolve o módulo ao estado de boot. */
export function resetarDecodersDeVideoEmUso(): void {
  decodersDeVideoEmUso = null;
}

export interface ParametrosDoSegundoDecoder {
  /** A plataforma comporta o A/B? (flag de build + plataforma) */
  disponivel: boolean;
  /** Mídia na tela. Serve só pra re-tentar a sonda quando ela é interrompida. */
  mediaId: string | null;
  /** Gatilho em cena: não disputar decoder com ele. */
  emGatilho: boolean;
  /** O 2º `<video>` (standby) — onde a sonda tenta decodificar. */
  obterStandby: () => ElementoSondavel | null;
  /** O `<video>` que está tocando. */
  obterAtivo: () => { readyState: number } | null;
  /** URL de um vídeo em cache; quem implementa também ajusta o crossOrigin do standby. */
  resolverUrl: () => Promise<string | null>;
}

/**
 * Decide se o motor agendado (A/B) pode ligar neste aparelho.
 *
 * <p><b>Seguro por padrão.</b> Nasce `'pendente'` — isto é, A/B DESLIGADO — e só liga depois
 * que a sonda consegue decodificar um quadro num segundo elemento de vídeo. Não conseguiu nas
 * {@link SONDA_TENTATIVAS} tentativas → `'desligado'` pelo resto da sessão, e o player segue
 * no motor reativo, que foi medido tocando normalmente com UM decoder.</p>
 *
 * <p><b>Por que não perguntar ao aparelho.</b> O Tab A7 declara 16 instâncias de AVC e entrega
 * 16 instâncias OCIOSAS — mas recusa bem antes quando elas precisam DECODIFICAR de verdade
 * (`InsufficientResources`). O teto que importa é de memória, não de contagem, então só tentar
 * decodificar diz a verdade. Medido em 2026-10-12.</p>
 *
 * <p><b>Interromper não reprova.</b> Se a mídia trocar (ou um gatilho entrar) no meio, o
 * veredito continua `'pendente'` e a próxima mídia sonda de novo. Sem isso, um aparelho
 * perfeitamente capaz perderia o A/B pela sessão inteira por acidente — e em silêncio.</p>
 *
 * @returns `true` quando o A/B pode operar.
 */
export function useSegundoDecoder({
  disponivel,
  mediaId,
  emGatilho,
  obterStandby,
  obterAtivo,
  resolverUrl,
}: ParametrosDoSegundoDecoder): boolean {
  const [veredito, setVeredito] = useState<VereditoDaSonda>('pendente');
  const emCursoRef = useRef(false);
  /**
   * A sonda sobrevive à troca de mídia. Ela precisa de ~15s pra concluir (6 tentativas) e os
   * itens costumam durar 10s — cancelar a cada troca fazia a sonda RECOMEÇAR pra sempre e
   * nunca dar veredito. Medido no Tab A7 em 2026-10-12: 49 tentativas em 2 minutos, zero
   * conclusões. Só o desmonte e a entrada de um gatilho a interrompem.
   */
  const vivoRef = useRef(true);
  const emGatilhoRef = useRef(emGatilho);
  emGatilhoRef.current = emGatilho;
  useEffect(
    () => () => {
      vivoRef.current = false;
    },
    [],
  );

  useEffect(() => {
    if (!disponivel || veredito !== 'pendente' || emCursoRef.current) return;
    if (!mediaId || emGatilho) return;
    const standby = obterStandby();
    if (!standby) return;

    emCursoRef.current = true;
    const cancelada = (): boolean => !vivoRef.current || emGatilhoRef.current;
    const esperar = (ms: number): Promise<void> =>
      new Promise((resolve) => setTimeout(resolve, ms));

    /**
     * Só sonda depois que o vídeo ativo já decodificou um quadro: é o cenário real da troca,
     * e sondar durante o load do primeiro vídeo disputaria o decoder justamente com quem
     * precisa dele — o contrário do que esta sonda existe pra evitar.
     */
    const aguardarAtivoPronto = async (): Promise<boolean> => {
      for (let i = 0; i < TENTATIVAS_DE_ESPERA; i += 1) {
        if (cancelada()) return false;
        const ativo = obterAtivo();
        if (ativo && ativo.readyState >= QUADRO_DECODIFICADO) return true;
        await esperar(ESPERA_PELO_ATIVO_MS);
      }
      return false;
    };

    void (async () => {
      try {
        if (!(await aguardarAtivoPronto())) return;
        const url = await resolverUrl();
        if (cancelada() || !url) return;
        const conseguiu = await sondarSegundoDecoder({
          elemento: standby,
          url,
          cancelado: cancelada,
          aoTentar: (n, total) =>
            console.log(`[Stream] 🔎 sondando o 2º decoder (${n}/${total})`),
        });
        if (cancelada()) return;
        if (conseguiu) {
          console.log(
            '[Stream] ✅ 2º decoder disponível — motor agendado (A/B) LIGADO',
          );
          setVeredito('ligado');
        } else {
          console.warn(
            `[Stream] ⚠️ aparelho não entregou um 2º decoder em ${SONDA_TENTATIVAS} tentativas — ` +
              'motor A/B DESLIGADO nesta sessão, seguindo no reativo com UM decoder',
          );
          setVeredito('desligado');
        }
      } catch (err) {
        console.warn('[Stream] sonda do 2º decoder falhou', err);
      } finally {
        emCursoRef.current = false;
      }
    })();

    // Sem cleanup que cancele: a troca de mídia re-roda este efeito, mas `emCursoRef` faz
    // ele sair na porta e a sonda em curso segue até o fim.
    // `obterStandby`/`obterAtivo`/`resolverUrl` leem refs — não entram nas deps de propósito.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disponivel, veredito, mediaId, emGatilho]);

  // Publica o estado para o snapshot de saúde (o admin mostra no cartão da versão).
  // Durante a sonda o veredito é 'pendente' e o player está, de fato, usando UM decoder —
  // então 1 é a resposta correta, não um "indefinido".
  decodersDeVideoEmUso = !disponivel ? null : veredito === 'ligado' ? 2 : 1;

  return veredito === 'ligado';
}
